import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScheduleProps {
  userId: string | undefined;
}

type ItemType = "treatment" | "back_assessment" | "arm_assessment" | "knee_assessment";

type PlanItem = {
  type: ItemType;
  completed?: boolean;
};

type PlanDay = {
  items: PlanItem[];
};

type ScheduleDoc = {
  streak: number;
  dateCreated: string;
  completedTreatments: number;
  recoveryPlan: string;
  days: PlanDay[];
  completedAssessments: number;
  totalTreatments: number;
  /** Status as stored in Firestore — we never write to this */
  status: "active" | "paused" | "completed" | "concluded" | string;
  totalAssessments: number;
  userId: string;
};

type FireSchedule = Partial<ScheduleDoc> & Record<string, unknown>;
type ScheduleWithId = { id: string; data: ScheduleDoc };

/**
 * Derived display status — computed purely from data, never written to Firestore.
 *
 *  "expired"    — dateCreated + days.length days is in the past (historical)
 *  "incomplete" — not expired, but completedTreatments < totalTreatments OR
 *                 at least one item has completed === false
 *  "active"     — not expired, not incomplete
 *  "paused"     — stored status is paused and not expired
 *  "completed"  — stored status is completed
 *  falls back to stored status for anything else
 */
type DerivedStatus = "active" | "expired" | "incomplete" | "paused" | "completed" | string;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const dotForType = (t: ItemType) =>
  t === "treatment" ? "bg-MyovoltPurple" : "bg-MyovoltBlue";

const labelForType = (t: ItemType): string => {
  switch (t) {
    case "treatment":       return "Treatment";
    case "back_assessment": return "Back Assessment";
    case "arm_assessment":  return "Arm Assessment";
    case "knee_assessment": return "Knee Assessment";
    default:                return t;
  }
};

const normalizeSchedule = (raw: FireSchedule): ScheduleDoc => {
  const rawDays = Array.isArray(raw.days) ? (raw.days as any[]) : [];
  const normDays: PlanDay[] = rawDays.map((day) => {
    const itemsArr = Array.isArray(day?.items) ? (day.items as any[]) : [];
    const normItems: PlanItem[] = itemsArr.map((it) => ({
      type: (it?.type as ItemType) ?? "treatment",
      completed: typeof it?.completed === "boolean" ? it.completed : undefined,
    }));
    return { items: normItems };
  });

  return {
    streak:               Number(raw.streak ?? 0),
    dateCreated:          typeof raw.dateCreated === "string" ? raw.dateCreated : "",
    completedTreatments:  Number(raw.completedTreatments ?? 0),
    recoveryPlan:         typeof raw.recoveryPlan === "string" ? raw.recoveryPlan : "",
    days:                 normDays,
    completedAssessments: Number(raw.completedAssessments ?? 0),
    totalTreatments:      Number(raw.totalTreatments ?? 0),
    status:               (raw.status as string) ?? "active",
    totalAssessments:     Number(raw.totalAssessments ?? 0),
    userId:               typeof raw.userId === "string" ? raw.userId : "",
  };
};

/**
 * Derive a display-only status from the schedule data.
 * This never touches Firestore.
 */
function deriveStatus(s: ScheduleDoc, now = new Date()): DerivedStatus {
  // Paused / completed are authoritative from Firestore
  if (s.status === "paused")    return "paused";
  if (s.status === "completed") return "completed";

  const start = new Date(s.dateCreated);
  const lenDays = s.days.length;

  // If we can compute an end date, check expiry
  if (!isNaN(start.getTime()) && lenDays > 0) {
    const end = new Date(start);
    end.setDate(end.getDate() + lenDays);
    if (now >= end) return "expired";
  }

  // Check for incomplete items within the days that have passed
  const daysPassed = !isNaN(start.getTime())
    ? Math.floor((now.getTime() - start.getTime()) / 86_400_000)
    : lenDays;

  const pastDays = s.days.slice(0, Math.min(daysPassed, lenDays));
  const hasIncomplete = pastDays.some((day) =>
    day.items.some((it) => it.completed === false)
  );

  if (hasIncomplete) return "incomplete";

  return "active";
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  active:     "bg-green-100 text-green-800",
  incomplete: "bg-amber-100 text-amber-800",
  expired:    "bg-gray-200 text-gray-600",
  paused:     "bg-blue-100 text-blue-800",
  completed:  "bg-purple-100 text-purple-800",
};

const StatusBadge: React.FC<{ status: DerivedStatus }> = ({ status }) => (
  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600"}`}>
    {status}
  </span>
);

// ─── Circle stat ──────────────────────────────────────────────────────────────

const CircleStat: React.FC<{ label: string; value: number; subtitle?: string }> = ({
  label, value, subtitle,
}) => {
  const size = 84, stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  const dash = c * clamped;

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 p-3">
      <svg width={size} height={size} className="shrink-0">
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} className="text-gray-200" stroke="currentColor" fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r} strokeWidth={stroke}
          className="text-gray-900" stroke="currentColor" fill="none"
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="14" className="fill-gray-900">
          {`${Math.round(clamped * 100)}%`}
        </text>
      </svg>
      <div>
        <div className="font-semibold text-sm">{label}</div>
        {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
};

// ─── Day card ─────────────────────────────────────────────────────────────────

const DayCard: React.FC<{
  day: PlanDay;
  idx: number;
  isToday: boolean;
  isPast: boolean;
}> = ({ day, idx, isToday, isPast }) => {
  const items = Array.isArray(day.items) ? day.items : [];

  const allDone   = items.length > 0 && items.every((it) => it.completed === true);
  const anyFailed = items.some((it) => it.completed === false);

  let borderClass = "border-gray-200";
  if (isToday)       borderClass = "border-MyovoltGreen ring-1 ring-MyovoltGreen";
  else if (allDone)  borderClass = "border-green-300 bg-green-50";
  else if (anyFailed && isPast) borderClass = "border-amber-300 bg-amber-50";
  else if (isPast)   borderClass = "border-gray-200 bg-gray-50";

  return (
    <div className={`rounded-2xl border p-3 ${borderClass}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-sm">Day {idx + 1}</span>
        {isToday && <span className="text-xs text-MyovoltGreen font-semibold">Today</span>}
        {!isToday && allDone && <span className="text-xs text-green-600">✓</span>}
        {!isToday && anyFailed && isPast && <span className="text-xs text-amber-600">!</span>}
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-gray-400">Rest day</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotForType(it.type)}`} />
              <span className="truncate flex-1">{labelForType(it.type)}</span>
              {typeof it.completed === "boolean" && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${
                  it.completed ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {it.completed ? "Done" : "Pending"}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const Schedule: React.FC<ScheduleProps> = ({ userId }) => {
  const [allSchedules, setAllSchedules] = useState<ScheduleWithId[]>([]);
  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(collection(db, "user_schedules_2"), where("userId", "==", userId))
      );
      const list: ScheduleWithId[] = [];
      snap.forEach((d: QueryDocumentSnapshot<DocumentData>) => {
        list.push({ id: d.id, data: normalizeSchedule(d.data() as FireSchedule) });
      });

      // Newest first
      list.sort((a, b) => {
        const da = new Date(a.data.dateCreated).getTime() || 0;
        const db_ = new Date(b.data.dateCreated).getTime() || 0;
        return db_ - da;
      });

      setAllSchedules(list);

      // Auto-select: prefer the first non-expired schedule, else most recent
      const now = new Date();
      const preferred = list.find((s) => deriveStatus(s.data, now) !== "expired");
      setSelectedId((preferred ?? list[0])?.id ?? null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load schedules.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => allSchedules.find((s) => s.id === selectedId) ?? null,
    [selectedId, allSchedules]
  );

  const derivedStatus = useMemo(
    () => (selected ? deriveStatus(selected.data) : null),
    [selected]
  );

  const totals = useMemo(() => {
    const s = selected?.data;
    if (!s) return { tPct: 0, aPct: 0 };
    return {
      tPct: s.totalTreatments  > 0 ? Math.min(1, s.completedTreatments  / s.totalTreatments)  : 0,
      aPct: s.totalAssessments > 0 ? Math.min(1, s.completedAssessments / s.totalAssessments) : 0,
    };
  }, [selected]);

  // Today's index within the selected schedule
  const todayIdx = useMemo(() => {
    if (!selected?.data.dateCreated) return -1;
    const start = new Date(selected.data.dateCreated);
    if (isNaN(start.getTime())) return -1;
    return Math.floor((Date.now() - start.getTime()) / 86_400_000);
  }, [selected]);

  const createdPretty = useMemo(() => {
    const s = selected?.data;
    if (!s?.dateCreated) return "";
    const d = new Date(s.dateCreated);
    return isNaN(d.getTime()) ? s.dateCreated : d.toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  }, [selected]);

  const endDatePretty = useMemo(() => {
    const s = selected?.data;
    if (!s?.dateCreated || !s.days.length) return "";
    const end = new Date(s.dateCreated);
    end.setDate(end.getDate() + s.days.length);
    return isNaN(end.getTime()) ? "" : end.toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  }, [selected]);

  return (
    <div className="w-full space-y-5">

      {/* ── Controls row ── */}
      <div className="flex flex-wrap items-end gap-3">
        <button className="btn btn-sm" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Schedule</label>
          <select
            className="select select-bordered select-sm min-w-[260px]"
            value={selectedId ?? ""}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={allSchedules.length === 0}
          >
            {allSchedules.length === 0 ? (
              <option value="" disabled>{loading ? "Loading…" : "No schedules"}</option>
            ) : (
              allSchedules.map((s) => {
                const ds = deriveStatus(s.data);
                const when = (() => {
                  const d = new Date(s.data.dateCreated);
                  return isNaN(d.getTime()) ? s.data.dateCreated : d.toLocaleDateString();
                })();
                return (
                  <option key={s.id} value={s.id}>
                    {s.data.recoveryPlan || "Unnamed"} — {when} [{ds}]
                  </option>
                );
              })
            )}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ── Selected schedule header ── */}
      {selected && derivedStatus && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-base font-semibold">{selected.data.recoveryPlan || "Unnamed plan"}</span>
            <StatusBadge status={derivedStatus} />
            {selected.data.streak > 0 && (
              <span className="text-sm text-orange-500 font-medium">🔥 {selected.data.streak} day streak</span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
            {createdPretty  && <span>Started: <span className="text-gray-700 font-medium">{createdPretty}</span></span>}
            {endDatePretty  && <span>Ends: <span className="text-gray-700 font-medium">{endDatePretty}</span></span>}
            <span>Days: <span className="text-gray-700 font-medium">{selected.data.days.length}</span></span>
          </div>

          {/* Status-specific callout */}
          {derivedStatus === "expired" && (
            <div className="rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600">
              This schedule ended on {endDatePretty}. Shown as historical record.
            </div>
          )}
          {derivedStatus === "incomplete" && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
              Some past days have pending items — the user may not have completed everything on schedule.
            </div>
          )}
          {derivedStatus === "paused" && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
              This schedule is currently paused.
            </div>
          )}

          {/* Progress rings */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <CircleStat
              label="Treatments"
              value={totals.tPct}
              subtitle={`${selected.data.completedTreatments} / ${selected.data.totalTreatments} completed`}
            />
            <CircleStat
              label="Assessments"
              value={totals.aPct}
              subtitle={`${selected.data.completedAssessments} / ${selected.data.totalAssessments} completed`}
            />
          </div>
        </div>
      )}

      {/* ── Days grid ── */}
      {!selected ? (
        <div className="text-sm text-gray-500">
          {loading ? "Loading schedules…" : "No schedule selected."}
        </div>
      ) : selected.data.days.length === 0 ? (
        <div className="text-sm text-gray-500">This schedule has no days.</div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-MyovoltPurple inline-block"/>Treatment</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-MyovoltBlue inline-block"/>Assessment</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-MyovoltGreen ring-1 ring-MyovoltGreen inline-block"/>Today</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300 inline-block"/>Missed items</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {selected.data.days.map((day, idx) => (
              <DayCard
                key={idx}
                day={day}
                idx={idx}
                isToday={idx === todayIdx}
                isPast={idx < todayIdx}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Schedule;